import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

// OIDC settings for the doit-v2 app.
//
// --- Why we DON'T use automaticSilentRenew ---------------------------------
//
// The doit Keycloak realm has unusually short session timeouts (SSO
// Session Max ~= 10 min on first observation). Keycloak caps every issued
// access token at `min(access_token_lifespan, session_max_remaining)`, so
// as a session approaches its absolute deadline, each renewed token
// arrives with a smaller and smaller `expires_in` (300s -> 131s -> 61s
// -> ...).
//
// oidc-client-ts's `automaticSilentRenew` schedules its renew based on
// the issued `expires_in - accessTokenExpiringNotificationTimeInSeconds`.
// Once that math goes <= 0, oidc-client-ts floors the timer to 1 ms and
// fires immediately, which produces the "/token + /userinfo running
// continuously" storm we hit. There is no notification-time value that
// avoids it on a realm that hands back shrinking tokens.
//
// So we drive renewal ourselves on a FIXED interval that ignores the
// token lifespan entirely. If renewals start failing (the realm session
// has died), we bail to login rather than spinning.
//
// The proper long-term fix is server-side: raise SSO Session Idle and
// SSO Session Max in the Keycloak `doit` realm to typical values (30
// min idle / 10 hr max). Do that and silent renewal will Just Work.
//
// --- Why the renewal driver is a module-scope singleton --------------------
//
// Vite HMR can hot-replace AuthProvider.tsx without firing the previous
// useEffect cleanup, leaving an orphaned setInterval alive alongside the
// new one. With multiple timers running, the next ticks race for the
// refresh token -- the first wins, Keycloak rotates the refresh_token,
// the others receive "Token is not active". A single module-level timer
// can't be duplicated by HMR or StrictMode, so the race goes away.

export const oidcConfig = {
  authority: 'https://portal-eroyal.pmdgtech.com/auth/realms/doit',
  client_id: 'web_app',
  redirect_uri: `${window.location.origin}${import.meta.env.BASE_URL}`,
  post_logout_redirect_uri: `${window.location.origin}${import.meta.env.BASE_URL}`,
  response_type: 'code',
  scope: 'openid profile email',

  // Driven by startProactiveRenewal / stopProactiveRenewal below.
  automaticSilentRenew: false,
  silent_redirect_uri: `${window.location.origin}${import.meta.env.BASE_URL}silent-renew`,
  silentRequestTimeoutInSeconds: 12,
  monitorSession: false,
  loadUserInfo: true,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
};

export const userManager = new UserManager(oidcConfig);

// Renewal interval. Default 4 minutes -- short enough to stay inside the
// realm's refresh window (currently observed at ~11 min), long enough to
// keep /token traffic minimal. Override per env via
// VITE_OIDC_RENEW_INTERVAL_MS (e.g. 60000 to renew every minute while
// debugging in DevTools).
const RENEW_INTERVAL_MS_OVERRIDE = Number(
  import.meta.env.VITE_OIDC_RENEW_INTERVAL_MS,
);
export const PROACTIVE_RENEW_INTERVAL_MS =
  Number.isFinite(RENEW_INTERVAL_MS_OVERRIDE) && RENEW_INTERVAL_MS_OVERRIDE > 0
    ? RENEW_INTERVAL_MS_OVERRIDE
    : 4 * 60 * 1000;

// ---------------------------------------------------------------------------
// Module-scope renewal singleton.
// ---------------------------------------------------------------------------

let renewTimerId: ReturnType<typeof setInterval> | null = null;
let renewInflight: Promise<unknown> | null = null;
let consecutiveFailures = 0;
const MAX_CONSECUTIVE_FAILURES = 3;

function bounceToLogin(reason: string): void {
  console.error(`[oidc] ${reason} -- bouncing user to login`);
  stopProactiveRenewal();
  userManager.removeUser().finally(() => {
    window.location.href = import.meta.env.BASE_URL || '/';
  });
}

async function runRenew(reason: string): Promise<void> {
  if (renewInflight) {
    console.log(`[oidc] renewal already in-flight, skipping (${reason})`);
    return;
  }
  console.log(`[oidc] renewing access token (${reason})...`);
  renewInflight = userManager
    .signinSilent()
    .then((user) => {
      consecutiveFailures = 0;
      if (!user) {
        console.warn('[oidc] renewal returned null user');
        return;
      }
      const exp = user.expires_at
        ? new Date(user.expires_at * 1000).toISOString()
        : 'unknown';
      console.log(`[oidc] renewal ok, new access_token expires at ${exp}`);

      // Defensive: if the realm hands us back a token whose entire
      // lifetime is shorter than our renew interval, the realm session
      // is approaching its hard deadline and silent renewal is about to
      // break down. Send the user to login while we still can.
      const intervalSec = Math.round(PROACTIVE_RENEW_INTERVAL_MS / 1000);
      if (
        user.expires_in !== undefined &&
        user.expires_in > 0 &&
        user.expires_in < intervalSec
      ) {
        bounceToLogin(
          `new token lives only ${user.expires_in}s (< ${intervalSec}s renew interval) -- realm session is ending`,
        );
      }
    })
    .catch((err) => {
      consecutiveFailures += 1;
      console.error(
        `[oidc] renewal failed (${consecutiveFailures}/${MAX_CONSECUTIVE_FAILURES}):`,
        err,
      );
      if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        bounceToLogin('three consecutive renewal failures');
      }
    })
    .finally(() => {
      renewInflight = null;
    });
  await renewInflight;
}

export function startProactiveRenewal(intervalMs = PROACTIVE_RENEW_INTERVAL_MS): void {
  // Clear any existing timer first -- this is what neutralises HMR
  // duplicates even when the previous effect's cleanup didn't fire.
  if (renewTimerId !== null) {
    clearInterval(renewTimerId);
    renewTimerId = null;
  }
  consecutiveFailures = 0;
  console.log(
    `[oidc] proactive renewal timer armed -- next renew in ${Math.round(
      intervalMs / 1000,
    )}s ` +
    `(call window.renewNow() to trigger one immediately)`,
  );
  renewTimerId = setInterval(() => {
    void runRenew('interval');
  }, intervalMs);
}

export function stopProactiveRenewal(): void {
  if (renewTimerId !== null) {
    console.log('[oidc] proactive renewal timer cleared');
    clearInterval(renewTimerId);
    renewTimerId = null;
  }
  consecutiveFailures = 0;
}

/** Manually trigger a renewal -- shared in-flight guard prevents stampedes. */
export function renewNow(): Promise<unknown> {
  return runRenew('manual');
}

if (import.meta.env.DEV) {
  (window as unknown as {
    userManager: UserManager;
    renewNow: typeof renewNow;
  }).userManager = userManager;
  (window as unknown as {
    userManager: UserManager;
    renewNow: typeof renewNow;
  }).renewNow = renewNow;
}
